import { Injectable } from '@angular/core';
import { Translation, TranslocoService } from '@jsverse/transloco';
import { AsyncSubject, lastValueFrom } from 'rxjs';

import { ILogger } from '@shure/shared/angular/utils/logging';

import { extractLabels } from '../extractLabels';

import { ScopeInfo, TranslationScopeService, translationScopeService } from './translation-scope.service';

interface LanguageState {
	isLoaded: boolean;
	loadingDoneSubject: AsyncSubject<void>;
}

/**
 * This class monitors translation files required by code currently loaded.
 *
 * It does so in concert with
 * - shUseTranslation decorator
 * - TranslationScopeService, tracking which label scopes are required to be
 *   loaded.
 *
 * This service will monitor lazy loading finishing, marked by a manual call
 * to translationScopeService.updateAfterImportingCode(), and pick up translations
 * required by the lazy loaded content.
 *
 * This service will monitor current language change in Transloco and load
 * any languages not yet loaded for the label scopes required by all code
 * which is currently loaded.
 *
 * To use in your app your app-module.ts must have
 * provideTranslationScopeLoaderInitializer() in its providers array.
 * Also all routes doing lazy loading in its app-routing.module.ts should have a
 * .then(importTranslations) call added at the end of the import() async doing
 * the lazy loading.
 */
@Injectable({ providedIn: 'root' })
export class TranslationScopeLoaderService {
	private translationScopeService: TranslationScopeService;
	private logger: ILogger;

	private started: boolean;
	private scopes: Map<string, ScopeInfo>;
	private currentLanguage: string;

	/**
	 * Languages which are loaded across all the required scopes
	 */
	private loadedLanguages: Map<string, LanguageState>;

	constructor(
		private readonly translocoService: TranslocoService,
		logger: ILogger
	) {
		this.translationScopeService = translationScopeService;
		this.logger = logger.createScopedLogger('TranslationScopeLoaderService');

		this.started = false;
		this.scopes = this.translationScopeService.scopes;
		this.loadedLanguages = new Map<string, LanguageState>();

		this.currentLanguage = this.translocoService.getActiveLang();
	}

	/**
	 * Start the monitoring of required translation files and loading of them when needed.
	 */
	public async start(): Promise<void> {
		if (this.started) {
			return;
		}

		// React to code requiring a new label scope
		//  (we can't load the labels here since we generally can't await in the code decorators
		//   typically used for requiring label scopes)
		this.translationScopeService.scopeAddedCallback = (scope): void => {
			this.logger.information('RequireScope', `Scope '${scope.name}' required`);
		};

		// React to code modules having been loaded which require new label scopes to be loaded
		this.translationScopeService.loadMissingLabelsCallback = async (): Promise<void> => {
			// All languages are missing for the new scope(s) added -> No languages are completely loaded
			this.loadedLanguages.clear();

			await this.loadMissingTranslations();
		};

		// React to the current language changing
		this.translocoService.langChanges$.subscribe(async (lang) => {
			if (lang === this.currentLanguage) {
				return;
			}

			this.currentLanguage = lang;
			await this.loadMissingTranslations();
		});

		// Perform initial translation loading (thru TranslationScopeService to affect change-detection logic)
		await this.translationScopeService.updateAfterImportingCode();
		this.started = true;
	}

	/**
	 * Load any required translation files not yet loaded
	 *
	 * @returns Promise which resolves when loading has finished
	 */
	private loadMissingTranslations(): Promise<void> {
		return this.loadLanguage(this.currentLanguage);
	}

	/**
	 * Load all required language files for a specific language
	 */
	private async loadLanguage(lang: string): Promise<void> {
		let languageState = this.loadedLanguages.get(lang);
		if (!languageState) {
			// This language has not been loaded for all label scopes - load any missing scopes
			const loadingSubjects: Promise<void>[] = [];

			languageState = {
				isLoaded: false,
				loadingDoneSubject: new AsyncSubject<void>()
			};
			this.loadedLanguages.set(lang, languageState);

			for (const scope of this.scopes.values()) {
				if (!scope.loadedLanguages.has(lang)) {
					loadingSubjects.push(this.loadScopeLanguage(scope, lang));
					scope.loadedLanguages.add(lang);
				}
			}

			await Promise.all(loadingSubjects);

			languageState.isLoaded = true;
			languageState.loadingDoneSubject.next();
			languageState.loadingDoneSubject.complete();
		} else if (!languageState.isLoaded) {
			// This language is currently loading in another async context - wait for it to complete
			await lastValueFrom(languageState.loadingDoneSubject);
		}
	}

	private getDefaultLanguage(): string {
		return this.translocoService.getDefaultLang();
	}

	/**
	 * Unconditionally load translations for a single language in a single label scope into memory
	 *
	 * @param lang Language to load ('en', 'de', etc.)
	 */
	private async loadScopeLanguage(scope: ScopeInfo, lang: string): Promise<void> {
		let rawImport: unknown;
		try {
			rawImport = await scope.loader(`${lang}.json`);
		} catch {
			this.logger.error(
				'LoadScopeLanguage',
				`Failed to load language '${lang}' for scope '${scope.name}'. Using empty label set.`
			);
			rawImport = {};

			// Ensure default labels are loaded to have fallback labels in place for failed language
			const defLang = this.getDefaultLanguage();
			if (defLang && defLang !== lang && !scope.loadedLanguages.has(defLang)) {
				await this.loadScopeLanguage(scope, defLang);
			}
		}

		const imported = <Translation>rawImport;
		// Workaround for json imports being placed under a "default" property
		// eslint-disable-next-line dot-notation
		const rawLabels = <Translation>(imported['default'] ?? imported);
		const labels = extractLabels(rawLabels);
		const flattened = this.flattenLabels(labels, scope.name);

		this.translocoService.setTranslation(flattened, lang);

		this.logger.information('LoadScopeLanguage', `Loaded language '${lang}' for scope '${scope.name}'`);
	}

	/**
	 * Flatten a hierarchical label tree to a single-level label list with compound label keys
	 */
	private flattenLabels(json: Translation, scopeName: string): Translation {
		const entries = {};
		this.flattenLabelsInternal(json, scopeName, entries);
		return entries;
	}

	private flattenLabelsInternal(json: Translation, scopeName: string, result: Translation): void {
		const entries = Object.entries(json);
		for (const [key, value] of entries) {
			const keyName = scopeName ? `${scopeName}.${key}` : key;
			if (typeof value === 'string') {
				result[keyName] = value;
			} else {
				this.flattenLabelsInternal(value, keyName, result);
			}
		}
	}
}
